import { useEffect, useState } from "react";
import { useIsMobile } from "../hooks/useIsMobile.ts";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Grid,
    Card,
    CardContent,
    Chip,
    Stack,
    Tab,
    Tabs,
    Box,
    ToggleButton,
    ToggleButtonGroup
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from "recharts";
import { teamPatternService } from "../api/service.ts";
import { BestGroup, MonthlyGroupData } from "../api/types";

type ViewMode = "overall" | "month";
type Source = "ALL" | "TRAINING" | "MATCH";

const LINE_COLORS = ["#1976d2", "#e53935"];

export default function BestGroupsDialog({
    open,
    onClose
}: {
    open: boolean;
    onClose: () => void;
}) {
    const isMobile = useIsMobile();
    const [activeTab, setActiveTab] = useState(0);
    const [groups, setGroups] = useState<BestGroup[]>([]);
    const [monthly, setMonthly] = useState<MonthlyGroupData | null>(null);
    const [loadingRanking, setLoadingRanking] = useState(false);
    const [loadingTrend, setLoadingTrend] = useState(false);
    const [viewMode, setViewMode] = useState<ViewMode>("overall");
    const [selectedMonth, setSelectedMonth] = useState<Dayjs | null>(dayjs());
    const [source, setSource] = useState<Source>("ALL");

    // Carica classifica al cambio di filtri o apertura
    useEffect(() => {
        if (!open) return;
        setLoadingRanking(true);
        const params: { month?: number; year?: number; source?: string } = { source };
        if (viewMode === "month" && selectedMonth) {
            params.month = selectedMonth.month() + 1;
            params.year = selectedMonth.year();
        }
        teamPatternService
            .getBestGroup(params)
            .then((data) => setGroups(data))
            .catch((err) => console.error("Errore classifica:", err))
            .finally(() => setLoadingRanking(false));
    }, [open, viewMode, selectedMonth, source]);

    // Carica trend mensile quando si apre il tab o cambia la sorgente
    useEffect(() => {
        if (!open || activeTab !== 1) return;
        setLoadingTrend(true);
        teamPatternService
            .getMonthlyTrend(source)
            .then((data) => setMonthly(data))
            .catch((err) => console.error("Errore trend:", err))
            .finally(() => setLoadingTrend(false));
    }, [open, activeTab, source]);

    // Trasforma i dati per Recharts: appiattisce scores nel punto mensile
    const chartData = monthly?.monthly.map((point) => ({
        month: point.month,
        ...point.scores
    })) ?? [];

    const filters = (
        <Stack spacing={2} sx={{ mb: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={(_, v) => { if (v) setViewMode(v as ViewMode); }}
                    size="small"
                >
                    <ToggleButton value="overall">Stagione</ToggleButton>
                    <ToggleButton value="month">Per mese</ToggleButton>
                </ToggleButtonGroup>

                {viewMode === "month" && (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Mese"
                            views={["year", "month"]}
                            value={selectedMonth}
                            onChange={(v) => setSelectedMonth(v)}
                            slotProps={{ textField: { size: "small", sx: { width: 160 } } }}
                        />
                    </LocalizationProvider>
                )}
            </Stack>

            <ToggleButtonGroup
                value={source}
                exclusive
                onChange={(_, v) => { if (v) setSource(v as Source); }}
                size="small"
            >
                <ToggleButton value="ALL">Tutto</ToggleButton>
                <ToggleButton value="TRAINING">Allenamenti</ToggleButton>
                <ToggleButton value="MATCH">Partite</ToggleButton>
            </ToggleButtonGroup>
        </Stack>
    );

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" fullScreen={isMobile}>
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    backgroundColor: "primary.main"
                }}
            >
                Migliori Gruppi
                <Button onClick={onClose} startIcon={<CloseIcon />} color="inherit">
                    Chiudi
                </Button>
            </DialogTitle>

            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
                    <Tab label="Classifica" />
                    <Tab label="Trend mensile" />
                </Tabs>
            </Box>

            <DialogContent dividers>
                {filters}

                {/* TAB CLASSIFICA */}
                {activeTab === 0 && (
                    loadingRanking ? (
                        <Stack alignItems="center" sx={{ py: 4 }}>
                            <CircularProgress />
                        </Stack>
                    ) : groups.length === 0 ? (
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                            Nessun gruppo trovato per il periodo selezionato.
                        </Typography>
                    ) : (
                        groups.map((group, index) => {
                            const groupNames = group.players.map((p) => p.nickname).join(" + ");
                            const supportPct = (group.support * 100).toFixed(0);
                            return (
                                <Accordion key={index} defaultExpanded={index === 0}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                            {groupNames}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ ml: 2, whiteSpace: "nowrap" }}
                                        >
                                            Frequenza: <strong>{supportPct}%</strong>
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            {group.players.map((p) => (
                                                <Grid item xs={12} sm={6} md={4} key={p.id}>
                                                    <Card sx={{ backgroundColor: "#f9f9f9", borderRadius: 2, boxShadow: 2 }}>
                                                        <CardContent>
                                                            <Typography variant="h6" gutterBottom>
                                                                {p.nickname}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {p.firstName} {p.lastName} ({p.gender})
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                Skill: {p.skill} (base {p.baseSkill})
                                                            </Typography>
                                                            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                                                                {p.roles.map((role) => (
                                                                    <Chip key={role} label={role} size="small" color="primary" />
                                                                ))}
                                                            </Stack>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })
                    )
                )}

                {/* TAB TREND MENSILE */}
                {activeTab === 1 && (
                    loadingTrend ? (
                        <Stack alignItems="center" sx={{ py: 4 }}>
                            <CircularProgress />
                        </Stack>
                    ) : !monthly || monthly.pairs.length === 0 ? (
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                            Dati insufficienti per il trend mensile.
                        </Typography>
                    ) : (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                                Frequenza % nei set vinti — stagione corrente
                            </Typography>
                            <ResponsiveContainer width="100%" height={320}>
                                <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                                    <YAxis unit="%" domain={[0, 100]} tick={{ fontSize: 12 }} />
                                    <Tooltip formatter={(value: number) => `${value}%`} />
                                    <Legend />
                                    {monthly.pairs.map((pair, i) => (
                                        <Line
                                            key={pair}
                                            type="monotone"
                                            dataKey={pair}
                                            stroke={LINE_COLORS[i % LINE_COLORS.length]}
                                            strokeWidth={2}
                                            dot={{ r: 4 }}
                                            activeDot={{ r: 6 }}
                                        />
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        </Box>
                    )
                )}
            </DialogContent>
        </Dialog>
    );
}
